import React, { cloneElement, useMemo, useState } from 'react';
import {
    AppBar,
    Avatar,
    Box,
    CircularProgress,
    Drawer,
    IconButton,
    List,
    ListItem,
    makeStyles,
    Toolbar,
    Tooltip,
    Typography,
} from '@material-ui/core';
import { alpha } from '@material-ui/core/styles';
import MenuIcon from '@material-ui/icons/Menu';
import VisibilityIcon from '@material-ui/icons/Visibility';
import CategoryIcon from '@material-ui/icons/Category';
import LabelIcon from '@material-ui/icons/Label';
import DashboardIcon from '@material-ui/icons/Dashboard';
import PeopleOutlineIcon from '@material-ui/icons/PeopleOutline';
import AssignmentTurnedInIcon from '@material-ui/icons/AssignmentTurnedIn';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import TuneIcon from '@material-ui/icons/Tune';
import { useAuthProvider, useTranslate } from 'react-admin';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
    ancs,
    ancsV2,
    dashboard,
    login,
    prestataires,
    product_types,
    thresholds,
    types,
} from '../../pages/paths';
import { useMapApiValidation } from '../avatarAnc/hooks/useMapApiValidation';
import { useIsMobile } from '../../hooks/useIsMobile';
import { CustomReducerState } from '../../redux/reducers';
import { userDisconnected } from '../../redux/actions/users';
import { AddToHomeScreenInfoButton } from '../pwa/AddToHomeScreenInfo';
import { APP_FONT_FAMILY } from '../../theme/customTheme';
import { CRM_COLORS, PAGE_MAX_WIDTH } from '../../theme/crmColors';
import { BREAKPOINTS } from '../../theme/breakpoints';
import MyAncLogo from '../UI/Brand/MyAncLogo';

const useStyles = makeStyles(() => ({
    shell: {
        minWidth: 0,
    },
    integratedHeader: {
        backgroundColor: CRM_COLORS.surface,
        borderBottom: `1px solid ${CRM_COLORS.border}`,
        boxShadow: 'none',
        position: 'sticky',
        top: 0,
        zIndex: 1000,
    },
    toolbar: {
        minHeight: 64,
        padding: '0 24px 0 12px',
        gap: 14,
        [`@media (max-width:${BREAKPOINTS.mobile}px)`]: {
            padding: '0 12px 0 4px',
            minHeight: 56,
            gap: 8,
        },
    },
    menuButton: {
        color: CRM_COLORS.secondary,
        borderRadius: 10,
        padding: 10,
        '&:hover': {
            backgroundColor: alpha(CRM_COLORS.secondary, 0.06),
        },
    },
    appPill: {
        padding: '4px 10px',
        borderRadius: 999,
        backgroundColor: '#eef3f8',
        color: CRM_COLORS.text.label,
        fontSize: '0.8125rem',
        fontWeight: 600,
        whiteSpace: 'nowrap',
        [`@media (max-width:${BREAKPOINTS.mobile}px)`]: {
            display: 'none',
        },
    },
    user: {
        marginLeft: 'auto',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        minWidth: 0,
    },
    userText: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        lineHeight: 1.25,
        minWidth: 0,
        [`@media (max-width:${BREAKPOINTS.mobile}px)`]: {
            display: 'none',
        },
    },
    userName: {
        fontSize: '0.875rem',
        fontWeight: 600,
        color: CRM_COLORS.text.primary,
        lineHeight: 1.25,
        whiteSpace: 'nowrap',
    },
    userCompany: {
        fontSize: '0.75rem',
        color: CRM_COLORS.text.secondary,
        lineHeight: 1.25,
        whiteSpace: 'nowrap',
    },
    avatar: {
        width: 36,
        height: 36,
        backgroundColor: CRM_COLORS.status.ok.background,
        color: CRM_COLORS.status.ok.color,
        fontSize: '0.8125rem',
        fontWeight: 700,
        flexShrink: 0,
    },
    band: {
        backgroundColor: CRM_COLORS.surface,
        borderBottom: `1px solid ${CRM_COLORS.border}`,
    },
    bandInner: {
        maxWidth: PAGE_MAX_WIDTH,
        margin: '0 auto',
        padding: '20px 24px',
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'flex-end',
        gap: 16,
        flexWrap: 'wrap',
        [`@media (max-width:${BREAKPOINTS.mobile}px)`]: {
            padding: '12px 16px',
            gap: 10,
        },
    },
    titleBlock: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        minWidth: 0,
    },
    eyebrow: {
        fontSize: '0.8125rem',
        fontWeight: 500,
        color: CRM_COLORS.text.secondary,
        lineHeight: 1.3,
    },
    title: {
        margin: 0,
        fontSize: '1.75rem',
        fontWeight: 700,
        letterSpacing: '-0.015em',
        lineHeight: 1.2,
        color: CRM_COLORS.secondary,
        [`@media (max-width:${BREAKPOINTS.mobile}px)`]: {
            fontSize: '1.25rem',
        },
    },
    actions: {
        marginLeft: 'auto',
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0,
        [`@media (max-width:${BREAKPOINTS.mobile}px)`]: {
            marginLeft: 0,
        },
    },
    drawer: {
        width: 288,
        maxWidth: '100%',
        boxSizing: 'border-box',
        overflowX: 'hidden',
    },
    drawerPaper: {
        width: 288,
        maxWidth: '100vw',
        backgroundColor: CRM_COLORS.surface,
        fontFamily: APP_FONT_FAMILY,
        borderRight: `1px solid ${CRM_COLORS.border}`,
        overflowX: 'hidden',
        boxSizing: 'border-box',
    },
    drawerContent: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        maxWidth: '100%',
        overflowX: 'hidden',
        boxSizing: 'border-box',
    },
    drawerHeader: {
        padding: '0 20px',
        minHeight: 64,
        display: 'flex',
        alignItems: 'center',
        borderBottom: `1px solid ${CRM_COLORS.border}`,
        boxSizing: 'border-box',
    },
    menuList: {
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        padding: '16px 12px',
        width: '100%',
        boxSizing: 'border-box',
    },
    menuSection: {
        padding: '4px 12px 8px',
        fontSize: '0.75rem',
        fontWeight: 700,
        letterSpacing: '.06em',
        textTransform: 'uppercase',
        color: CRM_COLORS.text.secondary,
        '$menuItem + &, $menuItemSelected + &': {
            paddingTop: 20,
        },
    },
    menuItem: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        minHeight: 44,
        margin: '2px 0',
        padding: '0 12px',
        borderRadius: 8,
        boxSizing: 'border-box',
        transition: 'background-color 0.15s ease',
        '&:hover': {
            backgroundColor: CRM_COLORS.surfaceMuted,
        },
    },
    menuItemSelected: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        minHeight: 44,
        margin: '2px 0',
        padding: '0 12px 0 9px',
        borderRadius: 8,
        boxSizing: 'border-box',
        backgroundColor: CRM_COLORS.navSelected,
        borderLeft: `3px solid ${CRM_COLORS.success}`,
        '&:hover': {
            backgroundColor: CRM_COLORS.navSelected,
        },
        '& $menuText': {
            color: CRM_COLORS.secondary,
            fontWeight: 700,
        },
        '& $menuSubtext': {
            color: CRM_COLORS.status.ok.color,
            fontWeight: 600,
        },
        '& $menuIcon': {
            color: CRM_COLORS.status.ok.color,
        },
    },
    menuIcon: {
        color: CRM_COLORS.text.secondary,
        fontSize: 20,
        flexShrink: 0,
    },
    menuText: {
        flex: '1 1 auto',
        minWidth: 0,
        color: CRM_COLORS.text.label,
        fontWeight: 500,
        fontSize: '0.9375rem',
        lineHeight: 1.3,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    menuSubtext: {
        color: CRM_COLORS.text.secondary,
        fontSize: '0.75rem',
        whiteSpace: 'nowrap',
        flexShrink: 0,
    },
    pwaSection: {
        padding: '0 12px 12px',
    },
    logoutSection: {
        padding: '12px 12px 12px 20px',
        borderTop: `1px solid ${CRM_COLORS.border}`,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        boxSizing: 'border-box',
    },
    logoutText: {
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        flex: 1,
    },
    logoutName: {
        fontSize: '0.875rem',
        fontWeight: 600,
        color: CRM_COLORS.text.primary,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    logoutCompany: {
        fontSize: '0.78rem',
        color: CRM_COLORS.text.secondary,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    logoutButton: {
        color: CRM_COLORS.text.secondary,
        borderRadius: 8,
    },
}));

interface IntegratedHeaderProps {
    currentPath?: string;
    title: string;
    /** Small line above the title: which part of the backoffice this page belongs to. */
    eyebrow?: string;
    actions?: React.ReactNode;
}

const IntegratedHeader: React.FC<IntegratedHeaderProps> = ({ currentPath, title, eyebrow, actions }) => {
    const classes = useStyles();
    const translate = useTranslate();
    const history = useHistory();
    const dispatch = useDispatch();
    const authProvider = useAuthProvider();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const { isValid, isChecking } = useMapApiValidation();
    const isMobile = useIsMobile();
    const userConnected = useSelector((state: CustomReducerState) => state.rikutec.users.userConnected);
    const schemaAndHostUrlRootServer = process.env.REACT_APP_SCHEMA_AND_HOST_URL_ROOT_SERVER;

    const userName = useMemo(() => {
        if (!userConnected) {
            return null;
        }

        const fullName = `${userConnected.firstName || ''} ${userConnected.lastName || ''}`.trim();
        return fullName || userConnected.username || userConnected.name || null;
    }, [userConnected]);

    const companyName = userConnected?.company?.name ?? null;

    const userImageSrc = userConnected?.image?.name
        ? `${schemaAndHostUrlRootServer}/files/${userConnected.image.name}`
        : undefined;

    const userInitials = useMemo(
        () =>
            (userName || '')
                .split(/\s+/)
                .filter(Boolean)
                .slice(0, 2)
                .map((word: string) => word[0].toUpperCase())
                .join(''),
        [userName],
    );

    const menuSections = [
        {
            title: translate('app.sidebar.sections.monitoring'),
            items: [
                {
                    path: dashboard,
                    icon: <DashboardIcon />,
                    text: translate('app.dashboard.title'),
                },
                {
                    path: ancs,
                    icon: <VisibilityIcon />,
                    text: translate('app.ancs.actions.realTimeMonitoring'),
                    subtext: translate('app.sidebar.realTimeShort'),
                },
                {
                    path: ancsV2,
                    icon: <AssignmentTurnedInIcon />,
                    text: translate('app.ancs.actions.scheduledMonitoring'),
                    subtext: translate('app.sidebar.every2HoursShort'),
                },
            ],
        },
        {
            title: translate('app.sidebar.sections.reference'),
            items: [
                {
                    path: product_types,
                    icon: <CategoryIcon />,
                    text: translate('app.ancs.actions.productTypology'),
                },
                {
                    path: types,
                    icon: <LabelIcon />,
                    text: translate('app.ancs.actions.types'),
                },
                {
                    path: thresholds,
                    icon: <TuneIcon />,
                    text: translate('app.ancs.actions.thresholds'),
                },
                {
                    path: prestataires,
                    icon: <PeopleOutlineIcon />,
                    text: translate('app.prestataires.menu'),
                },
            ],
        },
    ];

    // En móvil el menú solo muestra las vistas de monitoreo ANC; las de
    // configuración (Product type, Types, Threshold settings) se ocultan.
    const visibleSections = menuSections
        .map((section) => ({
            ...section,
            items: isMobile
                ? section.items.filter((item) => item.path === ancs || item.path === ancsV2)
                : section.items,
        }))
        .filter((section) => section.items.length > 0);

    const handleMenuClick = (path: string) => {
        history.push(path);
        setDrawerOpen(false);
    };

    const toggleDrawer = () => {
        setDrawerOpen(!drawerOpen);
    };

    const handleLogout = async () => {
        await authProvider.logout({});
        dispatch(userDisconnected.fn());
        setDrawerOpen(false);
        history.push(login);
    };

    return (
        <>
            <Box className={classes.shell}>
                <AppBar position='static' className={classes.integratedHeader} elevation={0}>
                    <Toolbar className={classes.toolbar}>
                        <IconButton
                            edge='start'
                            className={classes.menuButton}
                            onClick={toggleDrawer}
                            aria-label='menu'
                        >
                            <MenuIcon />
                        </IconButton>

                        <MyAncLogo />
                        <span className={classes.appPill}>{translate('app.sidebar.appName')}</span>

                        {userConnected && (
                            <Box className={classes.user}>
                                <Box className={classes.userText}>
                                    <Typography className={classes.userName}>{userName}</Typography>
                                    {companyName && (
                                        <Typography className={classes.userCompany}>{companyName}</Typography>
                                    )}
                                </Box>
                                <Avatar className={classes.avatar} src={userImageSrc} alt={userName || ''}>
                                    {userInitials}
                                </Avatar>
                            </Box>
                        )}
                    </Toolbar>
                </AppBar>

                <Box className={classes.band}>
                    <Box className={classes.bandInner}>
                        <Box className={classes.titleBlock}>
                            {eyebrow && <Typography className={classes.eyebrow}>{eyebrow}</Typography>}
                            <Typography className={classes.title} component='h1'>
                                {title}
                            </Typography>
                        </Box>

                        {actions && <Box className={classes.actions}>{actions}</Box>}
                    </Box>
                </Box>
            </Box>

            <Drawer
                anchor='left'
                open={drawerOpen}
                onClose={toggleDrawer}
                classes={{
                    paper: classes.drawerPaper,
                }}
            >
                <Box className={`${classes.drawer} ${classes.drawerContent}`}>
                    <Box className={classes.drawerHeader}>
                        <MyAncLogo />
                    </Box>

                    <List className={classes.menuList} component='nav'>
                        {visibleSections.map((section) => (
                            <React.Fragment key={section.title}>
                                <Typography className={classes.menuSection}>{section.title}</Typography>
                                {section.items.map((item: any) => {
                                    const needsValidation = item.path === ancsV2;
                                    const isDisabled = needsValidation && (!isValid || isChecking);

                                    return (
                                        <ListItem
                                            key={item.path}
                                            button
                                            onClick={() => handleMenuClick(item.path)}
                                            className={
                                                currentPath === item.path ? classes.menuItemSelected : classes.menuItem
                                            }
                                            aria-current={currentPath === item.path ? 'page' : undefined}
                                            disabled={isDisabled}
                                        >
                                            {cloneElement(item.icon, {
                                                className: classes.menuIcon,
                                            })}

                                            <Typography className={classes.menuText}>{item.text}</Typography>
                                            {needsValidation && isChecking && <CircularProgress size={14} />}
                                            {item.subtext && (
                                                <Typography className={classes.menuSubtext}>{item.subtext}</Typography>
                                            )}
                                        </ListItem>
                                    );
                                })}
                            </React.Fragment>
                        ))}
                    </List>

                    <Box className={classes.pwaSection}>
                        <AddToHomeScreenInfoButton variant='outlined' fullWidth />
                    </Box>

                    <Box className={classes.logoutSection}>
                        <Avatar className={classes.avatar} src={userImageSrc} alt={userName || ''}>
                            {userInitials}
                        </Avatar>
                        <Box className={classes.logoutText}>
                            <Typography className={classes.logoutName}>{userName}</Typography>
                            {companyName && <Typography className={classes.logoutCompany}>{companyName}</Typography>}
                        </Box>
                        <Tooltip title={translate('app.sidebar.disconnect')}>
                            <IconButton
                                className={classes.logoutButton}
                                onClick={handleLogout}
                                aria-label={translate('app.sidebar.disconnect')}
                            >
                                <ExitToAppIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>
            </Drawer>
        </>
    );
};

export default IntegratedHeader;
